import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { EventList } from 'src/library/abstracts/event-list.js';
import { startTaskStyles } from 'src/library/start-task-styles.js';
import '../../library/elements/d-action.js';
import '../../library/elements/d-progress-bar.js';
import '../../library/fields/d-expansion.js';
import '../elements/d-label.js';
import './d-list-section.js';
import type { StartTaskListItem } from './start-task-list-item';

/**
 *
 * STATUS OK
 */
@customElement('d-list-section-start-tasks')
export class DListSectionStartTasks extends EventList<StartTaskListItem> {
  static readonly styles = [
    startTaskStyles,
    css`
      :host {
        border-radius: 12px;
        padding-bottom: 2px;
      }
      :host([open]) .header {
        padding-top: 6px;
      }
      :host([open]) .label-wrapper {
        background-position-y: 4px;
      }
      :host([open]) d-label {
        font-size: 18px;
      }
    `,
  ];
  @property({ type: Boolean, reflect: true })
  open = false;
  @property({ type: Array })
  items: StartTaskListItem[] = [];
  @property({ type: String, attribute: 'page-name' })
  pageName = '';

  private get totalCount() {
    if (this.showAll) {
      return this.items.length;
    }
    return this.items.filter((item) => {
      return item.eventCurrentUser;
    }).length;
  }

  private get doneCount() {
    if (this.showAll) {
      return this.items.filter((item) => {
        return item.eventDone;
      }).length;
    }
    return this.items.filter((item) => {
      return item.eventDone && item.eventCurrentUser;
    }).length;
  }

  private get progressText() {
    if (this.doneCount === this.totalCount) {
      return this.showAll ? t('library.startTasks.allDoneAll') : t('library.startTasks.allDoneMine');
    }
    return this.showAll
      ? t('library.startTasks.countDoneAll', { done: this.doneCount, total: this.totalCount })
      : t('library.startTasks.countDoneMine', { done: this.doneCount, total: this.totalCount });
  }

  private get progressTextFull() {
    const themePage = this.pageName ? t('library.startTasks.forThemePage', { pageName: this.pageName }) : '';
    if (this.doneCount === this.totalCount) {
      return this.showAll
        ? t('library.startTasks.allDoneFullAll', { themePage })
        : t('library.startTasks.allDoneFullMine', { themePage });
    }
    return this.showAll
      ? t('library.startTasks.countDoneFullAll', { done: this.doneCount, total: this.totalCount, themePage })
      : t('library.startTasks.countDoneFullMine', { done: this.doneCount, total: this.totalCount, themePage });
  }

  _open() {
    this.open = true;
  }

  _close(e) {
    e.stopPropagation();
    this.open = false;
  }

  _toggleShowAll() {
    console.log('_toggleShowAll');
    this.showAll = !this.showAll;
  }

  render() {
    return html`
      <div @click=${() => this._open()}>
        <div class="header">
          ${
            this.open
              ? html`
                  <div class="top-right">
                    <d-action @click=${() => this._toggleShowAll()}
                      >${this.showAll
                        ? t('library.startTasks.showOnlyMine')
                        : t('library.startTasks.showEveryones')}</d-action
                    >
                    <d-closer @click=${(e) => this._close(e)}></d-closer>
                  </div>
                `
              : nothing
          }
          </d-expansion>
          <div class="label-wrapper">
            <d-label semibold field="tasks_startTasks"></d-label>
            <div class="header-progress">
              <div class="progress-text">${this.progressText}</div>
              <d-progress-bar .total=${this.totalCount} .partNumber=${this.doneCount}></d-progress-bar>
            </div>
          </div>
        </div>
        <d-expansion ?opened=${this.open}>
          <div class="progress">
            <d-progress-bar .total=${this.totalCount} .partNumber=${this.doneCount}></d-progress-bar>
            <div class="progress-text">${this.progressTextFull}</div>
          </div>
          <d-list-section
            .items=${this._eventsWithFilterProperties(this.items).map((e) => ({
              ...e,
              clickData: e.uuid,
            }))}
            bordered
            no-header
            start-tasks
            @item-clicked=${this.onItemClicked}
          ></d-list-section>
        </d-expansion>
      </div>
    `;
  }

  private onItemClicked(e: CustomEvent<{ clickData: string }>) {
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent<{ uuid: string }>('start-task-selected', {
        bubbles: true,
        composed: true,
        detail: { uuid: e.detail.clickData },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-list-section-start-tasks': DListSectionStartTasks;
  }
}
